/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewEncapsulation
} from '@angular/core';
import {createMissingDateImplError} from './datepicker-errors';
import {matRangeDatepickerIntl} from './datepicker-intl';
import {DateAdapter, MAT_DATE_FORMATS, MatDateFormats} from '../datetime';
import {NgRedux, select} from '@angular-redux/store';
import {IAppState, UserPreference} from '../../../../store/IAppState';
import {
  dateInMonths,
  getAbbreviatedFiscalDateRange,
  getDateRangeBySelectedOption,
  getDefaultEndDate,
  getDefaultStartDate,
  getMonthFromNumberOfMonths,
  getYearFromNumberOfMonths
} from '../../../helpers';
import {FiscalQuarterOption} from '../../../models';
import {MatDialog} from '@angular/material/dialog';
import {ChangeFiscalYearComponent} from './change-fiscal-year/change-fiscal-year.component';
import {Observable, Subscription} from 'rxjs';
import {DateRangeOption} from '../../../enums';
import {getAppConfigValue} from '../../../helpers';
import {AppConfigEntries} from '../../../app-config-settings-enum';


/**
 * Possible views for the calendar.
 * @docs-private
 */
export type SatCalendarView = 'month' | 'year' | 'multi-year';

/* tslint:disable */
/**
 * A calendar that is used as part of the datepicker.
 * @docs-private
 */
@Component({
  // moduleId: module.id,
  selector: 'range-calendar',
  templateUrl: 'range-calendar.html',
  styleUrls: ['calendar.css'],
  host: {
    'class': 'range-calendar'
  },
  exportAs: 'matRangeCalendar',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RangeCalendar<D> implements OnInit, OnDestroy {
  @Input() id: any;
  @Input() ngClass: any;
  @Input() startAt: any;
  @Input() startView: any;
  @Input() minDate: any;
  @Input() maxDate: any;
  @Input() dateFilter: any;
  @Input() beginDate: D;
  @Input() endDate: D;
  @Input() beginMonth: any;
  @Input() endMonth: any;
  @Input() rangeMode: any;
  @Input() typeMode: any;
  @Input() selected: any;
  @Input() filterBannerFlag: boolean = false;
  @Input() selectedDateRangeOption: DateRangeOption;
  @Output() selectedChange: any = new EventEmitter();
  @Output() dateRangesChange: any = new EventEmitter();
  @Output() monthRangesChange: any = new EventEmitter();
  @Output() yearSelected: any = new EventEmitter();
  @Output() monthSelected: any = new EventEmitter();
  @Output() _userSelection: any = new EventEmitter();
  @Output() monthRangeChange: any = new EventEmitter();
  @select(['userPreferences'])
  private readonly userPreferences$: Observable<UserPreference>;
  /** Whenever user already selected start of dates interval. */
  private _beginDateSelected = false;
  private _beginMonthSelected = false;
  userPreferences: UserPreference;
  hideRecentMonth = false;
  showFiscalQuarterDropdown = false;
  showFiscalQuarterHover = false;
  fiscalQuarterOptions: FiscalQuarterOption[] = [
    {text: 'Q1', option: DateRangeOption.FiscalYearQ1, selected: false},
    {text: 'Q2', option: DateRangeOption.FiscalYearQ2, selected: false},
    {text: 'Q3', option: DateRangeOption.FiscalYearQ3, selected: false},
    {text: 'Q4', option: DateRangeOption.FiscalYearQ4, selected: false}
  ];
  DateRangeOption = DateRangeOption;
  fiscalYears: number[] = [];
  selectedQuarter = this.fiscalQuarterOptions[0];
  numberOfQuartersSelected = 1;
  selectedFiscalYear = 0;
  selectedFiscalQuartersText = 'Q1';
  quarterFiscalLabel = '';
  ytdFiscalLabel = '';
  userPreferenceSubscription: Subscription;
  dialogAfterClosedSubscription: Subscription;

  constructor(
    _intl: matRangeDatepickerIntl,
    @Optional() private _dateAdapter: DateAdapter<D>, private _changeDetectorRef: ChangeDetectorRef,
    @Optional() @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    private readonly ngRedux: NgRedux<IAppState>,
    public dialog: MatDialog
  ) {

    if (!this._dateAdapter) {
      throw createMissingDateImplError('DateAdapter');
    }

    if (!this._dateFormats) {
      throw createMissingDateImplError('MAT_DATE_FORMATS');
    }
  }

  ngOnInit(): void {
    this.setDefaultQuickDates();
    this.setUpFiscalVariables();

    this.userPreferenceSubscription = this.userPreferences$.subscribe((userPreferences: UserPreference) => {
      this.userPreferences = userPreferences;
      const fiscalMonthChanged = this.userPreferences.fiscalStartMonth !== userPreferences.fiscalStartMonth;

      if (fiscalMonthChanged
        && this.selectedDateRangeOption >= DateRangeOption.FiscalYearToDate
        && this.selectedDateRangeOption <= DateRangeOption.FiscalYearQ4) {
        this.selectDateOption(this.selectedDateRangeOption);
      } else if (!this.selectedDateRangeOption
        && (!this.beginDate || !this.endDate)
        && userPreferences?.dateRangeOption) {
        this.selectedDateRangeOption = userPreferences.dateRangeOption;
        this.selectDateOption(userPreferences.dateRangeOption);
      }
        this.setUpFiscalVariables();
    });
  }

  ngOnDestroy() {
    this.userPreferenceSubscription?.unsubscribe();
    this.dialogAfterClosedSubscription?.unsubscribe();
  }

  setUpFiscalVariables(): void { // TODO: Add logic to set correct fiscal quarter/year
    this.fiscalYears = [];
    const yearNow = new Date().getFullYear();
    const offset = getAppConfigValue(AppConfigEntries.ARCHIVE_YEAR_OFFSET, this.ngRedux.getState().data.applicationConfigurationSettings);
    for (let x = 0; x < (Number(offset) || 4); x++) {
      this.fiscalYears.push(yearNow - x);
    }
    this.selectedFiscalYear = this.fiscalYears[0];
    if (this.selectedDateRangeOption >= DateRangeOption.FiscalYearQ1 && this.selectedDateRangeOption <= DateRangeOption.FiscalYearQ4) {
      const foundQuarter = this.fiscalQuarterOptions.find(x => x.option === this.selectedDateRangeOption);
      this.selectedQuarter = foundQuarter ? foundQuarter : this.fiscalQuarterOptions[0];
      const startInMonths = dateInMonths(this._dateAdapter.getYear(this.beginDate), this._dateAdapter.getMonth(this.beginDate) + 1);
      const endInMonths = dateInMonths(this._dateAdapter.getYear(this.endDate), this._dateAdapter.getMonth(this.endDate) + 1);
      this.numberOfQuartersSelected = (endInMonths - startInMonths + 1) / 3;
      this.selectInitialFiscalQuarters(startInMonths);
    }
  }

  selectInitialFiscalQuarters(startInMonths: number): void {
    for (let optionIndex = 0; optionIndex < this.fiscalQuarterOptions.length; optionIndex++) {
      if (this.fiscalQuarterOptions[optionIndex].option === this.selectedDateRangeOption) {
        const fiscalStartInMonths = startInMonths - optionIndex * 3;
        this.selectedFiscalYear = getYearFromNumberOfMonths(fiscalStartInMonths);
        this.fiscalQuarterOptions[optionIndex].selected = true;
        this.selectedQuarter = this.fiscalQuarterOptions[optionIndex];
        this.selectedFiscalQuartersText = this.selectedQuarter.text;
        for (let i = 1; i < this.numberOfQuartersSelected; i++) {
          this.fiscalQuarterOptions[optionIndex + i].selected = true;
          this.selectedFiscalQuartersText += ', ' + this.fiscalQuarterOptions[optionIndex + i].text;
        }
        break;
      }
    }
    this.selectDateOption(this.selectedQuarter.option);
  }

  _dateSelected(date: D): void {
    if (this.rangeMode) {
      if (!this._beginDateSelected) {
        this._beginDateSelected = true;
        this.beginDate = date;
        this.endDate = date;
      } else {
        this._beginDateSelected = false;
        if (this._dateAdapter.compareDate(<D>this.beginDate, date) <= 0) {
          this.dateRangesChange.emit(
            {begin: <D>this.beginDate, end: date, selectedDateRangeOption: this.selectedDateRangeOption});
        } else {
          this.dateRangesChange.emit(
            {begin: date, end: <D>this.beginDate, selectedDateRangeOption: this.selectedDateRangeOption});
        }
      }
    } else if (!this._dateAdapter.sameDate(date, this.selected)) {
      this.selectedChange.emit(date);
    }
  }

  _rangeMonthSelected(date: D): void {
    if (this.rangeMode) {
      if (!this._beginMonthSelected) {
        this._beginMonthSelected = true;
        this.beginMonth = date;
        this.endMonth = date;
        this.monthRangesChange.emit({begin: <D>this.beginMonth, end: date});
      } else {
        this._beginMonthSelected = false;
        if (this._dateAdapter.compareDate(<D>this.beginMonth, date) <= 0) {
          this.monthRangesChange.emit({begin: <D>this.beginMonth, end: date});
        } else {
          this.monthRangesChange.emit({begin: date, end: <D>this.beginMonth});
        }
      }
    } else if (!this._dateAdapter.sameDate(date, this.selected)) {
      this.selectedChange.emit(date);
    }
  }

  _getOptionDates(dateRange: DateRangeOption) {
    let begin, end: any;
    let recentMonth, fiscalStartMonth, currentMonth, currentYear, endMonth, endYear: number;
    switch (dateRange) {
      case DateRangeOption.Past12Months:
        let beginYear = getDefaultStartDate().getFullYear();
        let beginMonth = getDefaultStartDate().getMonth();
        endYear = getDefaultEndDate().getFullYear();
        endMonth = getDefaultEndDate().getMonth();
        beginMonth++;
        if (beginMonth > 12) {
          beginMonth %= 12;
          ++beginYear;
        }
        endMonth++;
        if (endMonth > 12) {
          endMonth %= 12;
          ++endYear;
        }
        begin = this.getDate(beginYear, beginMonth);
        end = this.getDate(endYear, endMonth);
        return ({begin, end, selectedDateRangeOption: dateRange});
      case DateRangeOption.YearToDate:
        currentMonth = new Date().getMonth();
        currentYear = new Date().getFullYear();
        begin = this.getDate(currentYear, 1);
        end = currentMonth === 0 ? begin : this.getDate(currentYear, currentMonth);
        return ({begin, end, selectedDateRangeOption: dateRange});
      case DateRangeOption.RecentMonth:
        recentMonth = this.ngRedux.getState().display.recentMonth;
        if (recentMonth > 0) {
          begin = this.getDate(getYearFromNumberOfMonths(recentMonth), getMonthFromNumberOfMonths(recentMonth));
          end = this.getDate(getYearFromNumberOfMonths(recentMonth), getMonthFromNumberOfMonths(recentMonth));
          return ({begin, end, selectedDateRangeOption: dateRange});
        }
        this.hideRecentMonth = true;
        break;
      case DateRangeOption.Past12MonthsOfData:
        recentMonth = this.ngRedux.getState().display.recentMonth;
        if (recentMonth > 0) {
          begin = this.getDate(getYearFromNumberOfMonths(recentMonth - 11), getMonthFromNumberOfMonths(recentMonth - 11));
          end = this.getDate(getYearFromNumberOfMonths(recentMonth), getMonthFromNumberOfMonths(recentMonth));
          return ({begin, end, selectedDateRangeOption: dateRange});
        }
        this.hideRecentMonth = true;
        break;
      case DateRangeOption.FiscalYearToDate:
        fiscalStartMonth = this.userPreferences?.fiscalStartMonth || 1;
        currentYear = new Date().getFullYear();
        currentMonth = new Date().getMonth() + 1;
        endMonth = fiscalStartMonth === currentMonth ? currentMonth : currentMonth - 1;
        const startYear = fiscalStartMonth <= currentMonth ? currentYear : currentYear - 1;
        endYear = endMonth === 0 ? currentYear - 1 : currentYear;
        this.ytdFiscalLabel = getAbbreviatedFiscalDateRange({startYear, startMonth: fiscalStartMonth, endYear: currentYear, endMonth});
        begin = this.getDate(startYear, fiscalStartMonth);
        end = this.getDate(endYear, endMonth);
        return ({begin, end, selectedDateRangeOption: dateRange});
      case DateRangeOption.FiscalYearQ1:
      case DateRangeOption.FiscalYearQ2:
      case DateRangeOption.FiscalYearQ3:
      case DateRangeOption.FiscalYearQ4:
        fiscalStartMonth = this.userPreferences?.fiscalStartMonth || 1;
        const quartersRange = getDateRangeBySelectedOption(this.selectedDateRangeOption, 0, fiscalStartMonth, this.selectedFiscalYear, this.numberOfQuartersSelected * 3);

        this.quarterFiscalLabel = getAbbreviatedFiscalDateRange(quartersRange);
        this._changeDetectorRef.detectChanges();
        begin = this.getDate(quartersRange.startYear, quartersRange.startMonth);
        end = this.getDate(quartersRange.endYear, quartersRange.endMonth);
        return ({begin, end, selectedDateRangeOption: dateRange});
      default:
        break;
    }
  }

  toggleFiscalQuarterOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showFiscalQuarterDropdown = !this.showFiscalQuarterDropdown;
  }

  closeFiscalQuarterOptions(): void {
    this.showFiscalQuarterDropdown = false;
    this.showFiscalQuarterHover = false;
  }

  openChangeFiscalYear(event: MouseEvent): void {
    this.dialogAfterClosedSubscription = this.dialog.open(ChangeFiscalYearComponent, {})
      .afterClosed().subscribe(result => {
        if(result.isDialogCancelled) {
          event.stopPropagation();
          return;
        }
        this._getOptionDates(DateRangeOption.FiscalYearToDate);
        this.dateRangesChange.emit(this._getOptionDates(DateRangeOption.FiscalYearToDate));
        this.monthRangesChange.emit(this._getOptionDates(DateRangeOption.FiscalYearToDate));
        this._changeDetectorRef.detectChanges();
      });
  }

  getDate(year: number, month: number): D {
    return this._dateAdapter.createDate(year, ((12 + month) - 1) % 12, 1);
  }

  selectDateOption(dateRange: DateRangeOption) {
    this.selectedDateRangeOption = dateRange;
    if (![DateRangeOption.FiscalYearQ1, DateRangeOption.FiscalYearQ2, DateRangeOption.FiscalYearQ3,
      DateRangeOption.FiscalYearQ4].includes(dateRange)) {
      this.fiscalQuarterOptions.forEach(o => {
        o.selected = false;
      });
      this.showFiscalQuarterDropdown = false;
      this.quarterFiscalLabel = 'Q1';
      this.selectedFiscalQuartersText = 'Q1';
      this.numberOfQuartersSelected = 0;
    }
    this.dateRangesChange.emit(this._getOptionDates(dateRange));
    this.monthRangesChange.emit(this._getOptionDates(dateRange));
  }

  selectFiscalQuarter(option: FiscalQuarterOption, event?: MouseEvent | undefined, numberQuarters?: number): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    option.selected = !option.selected;
    if (!this.consecutiveQuartersSelected()) {
      option.selected = !option.selected;
      this.showFiscalQuarterHover = true;
    } else {
      const selectedQuarters = this.fiscalQuarterOptions.filter(o => o.selected);
      this.selectedQuarter = selectedQuarters[0] || this.fiscalQuarterOptions[0];
      this.numberOfQuartersSelected = numberQuarters || selectedQuarters.length;
      this.selectedFiscalQuartersText = this.numberOfQuartersSelected
        ? selectedQuarters.map(o => o.text).join(', ')
        : this.selectedQuarter.text;

      this.selectDateOption(this.selectedQuarter.option);
      this.showFiscalQuarterHover = false;
    }
  }

  selectFiscalYear(year: number, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const foundYear = this.fiscalYears.find(y => y === year);
    this.selectedFiscalYear = foundYear ? foundYear : this.fiscalYears[0];

    // recalculate start and end dates based on the new year and existing date-range-options??

    this.selectDateOption(this.selectedQuarter.option);
  }

  setDefaultQuickDates() {
    this.dateRangesChange.emit({begin: this.beginDate, end: this.endDate});
    this.monthRangesChange.emit({begin: this.beginDate, end: this.endDate});
  }

  consecutiveQuartersSelected(): boolean {
    if (!this.numberOfQuartersSelected) {
      return true;
    }
    let consecutiveSelected = true;
    this.fiscalQuarterOptions.forEach((option, i) => {
      const selectedQuarter = this.selectedQuarter.option;
      const quarter = option.option;
      if (option.selected && (quarter < selectedQuarter - 1 || selectedQuarter + this.numberOfQuartersSelected < quarter)) {
        consecutiveSelected = false;
      } else if (!option.selected && quarter > selectedQuarter && quarter < this.numberOfQuartersSelected + selectedQuarter - 1) {
        consecutiveSelected = false;
      }
    });
    return consecutiveSelected;
  }

  onCancel() {
    this._userSelection.emit();
  }

  onApply() {
    this._userSelection.emit({apply: true});
  }
}
